import React, {
  FC,
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import Link from "next/link";

import {
  SidebarHoverArrowTrigger,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  useSidebar,
} from "@src/lib/ui/Sidebar";
import { cn } from "@src/lib/utils/cn";
import LeftNavOrgSwitcher from "@src/features/app/left-nav/LeftNavOrgSwitcher";
import AdminLeftNavNewButton from "@src/features/app/left-nav/admin/AdminLeftNavNewButton";
import AdminRailAgentsSection from "@src/features/app/left-nav/admin/AdminRailAgentsSection";
import AnnouncementLetter from "./AnnouncementLetter";
import { useOperationsVisited } from "@src/features/app/left-nav/admin/operationsVisited";
import useNewMessagingEnabled from "@src/features/messaging/posts/useNewMessagingEnabled";
import useMessagingActivityUnreadCount from "@src/features/messaging/new/activity/useMessagingActivityUnreadCount";
import PlusIcon from "@src/lib/icons/18px/plus";
import FileTextOutlineIcon from "@src/lib/icons/18px/file-text-outline";
import useHasCustomerFacingAccountPlan from "@src/features/settings/accountPlan/useHasCustomerFacingAccountPlan";
import { useRouter } from "next/router";

import {
  AdminNavSectionId,
  AdminPrimaryNavItem,
  adminSettingsNavItem,
} from "./adminNavConfig";

type Props = {
  items: AdminPrimaryNavItem[];
  activeSectionId: AdminNavSectionId;
  onSelectSection: (sectionId: AdminNavSectionId) => void;
  // When true (e.g. on screens without a secondary menu), keep the rail
  // permanently expanded and skip the floating-overlay treatment so it
  // behaves as a real persistent column.
  forceExpanded?: boolean;
  /**
   * Shows the hover chevron on the rail's right edge: collapses the
   * force-expanded rail, or pins a collapsed one open again.
   */
  onToggleCollapsed?: () => void;
  hideNewButton?: boolean;
  hideSettings?: boolean;
  /** First settings page the user can open; null hides Settings. */
  settingsHref?: string | null;
};

const PRIMARY_RAIL_MENU_BUTTON_CLASS = cn(
  "relative isolate h-8 gap-4 px-1.5 text-s-v4 text-gray-11",
  "group-data-[collapsible=icon]:!size-8 group-data-[collapsible=icon]:!p-1.5 group-data-[collapsible=icon]:!text-s-v4",
  "[&>svg]:!size-4 [&>svg]:translate-x-[2.5px]",
  "data-[active=true]:!bg-bg-primary data-[active=true]:!shadow-default data-[active=true]:hover:!bg-bg-primary",
  // Labels: fade in 150ms when the rail expands; disappear instantly on
  // collapse (the 80ms width transition + overflow-hidden on the rail clip
  // anything still painting).
  "[&>span:last-child]:opacity-100 [&>span:last-child]:transition-opacity [&>span:last-child]:duration-150 [&>span:last-child]:ease-in-out [&>span:last-child]:motion-reduce:transition-none",
  "group-data-[collapsible=icon]:[&>span:last-child]:opacity-0 group-data-[collapsible=icon]:[&>span:last-child]:!transition-none",
);

const AdminPrimaryNavRail: FC<Props> = ({
  items,
  activeSectionId,
  onSelectSection,
  forceExpanded = false,
  onToggleCollapsed,
  hideNewButton = false,
  hideSettings = false,
  settingsHref = null,
}) => {
  const { primaryRailPinned: pinnedPreference, isMobile } = useSidebar();
  // A pin never widens the rail on a narrow window.
  const primaryRailPinned = pinnedPreference && !isMobile;
  const [hovered, setHovered] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  const expanded = forceExpanded || hovered || primaryRailPinned || menuOpen;
  // Floating overlay treatment (white card + shadow) only applies when the rail
  // is acting as a transient hover/pinned panel over a secondary menu.
  const floating = expanded && !forceExpanded;

  const openRail = () => {
    setHovered(true);
  };

  // When the rail stops being force-expanded (Hide, ⌘B, a section with a
  // secondary menu), drop any hover from the expanded column so it doesn't
  // linger as an overlay. If the pointer is still over it, React can read the
  // browser's follow-up mouseover (no relatedTarget) as a fresh enter, so
  // ignore enters until the pointer leaves.
  const navRef = useRef<HTMLElement>(null);
  const ignoreMouseEnter = useRef(false);
  const wasForceExpanded = useRef(forceExpanded);
  useLayoutEffect(() => {
    if (wasForceExpanded.current && !forceExpanded) {
      setHovered(false);
      ignoreMouseEnter.current = !!navRef.current?.matches(":hover");
    }
    wasForceExpanded.current = forceExpanded;
  }, [forceExpanded]);

  const closeRail = () => {
    if (menuOpen) return;
    setHovered(false);
  };

  // The letter's dialog portals out but stays inside the nav in React's
  // tree, so leaving the rail for it never fires onMouseLeave. Once it
  // closes, collapse on the next pointer move outside the rail; a keyboard
  // close leaves the rail open around the refocused trigger until it blurs.
  const collapseAfterLetter = useRef<((event: PointerEvent) => void) | null>(
    null,
  );
  const stopCollapseAfterLetter = () => {
    if (!collapseAfterLetter.current) return;
    document.removeEventListener("pointermove", collapseAfterLetter.current);
    collapseAfterLetter.current = null;
  };
  const onLetterOpenChange = useCallback((open: boolean) => {
    setMenuOpen(open);
    stopCollapseAfterLetter();
    if (open) return;
    const collapse = (event: PointerEvent) => {
      stopCollapseAfterLetter();
      if (!navRef.current?.contains(event.target as Node)) setHovered(false);
    };
    collapseAfterLetter.current = collapse;
    document.addEventListener("pointermove", collapse);
  }, []);
  useEffect(() => stopCollapseAfterLetter, []);

  const collapseTransientRail = () => {
    if (primaryRailPinned) return;
    setHovered(false);
  };

  const selectSection = (sectionId: AdminNavSectionId) => {
    onSelectSection(sectionId);
    collapseTransientRail();
  };

  return (
    <nav
      ref={navRef}
      aria-label="Main"
      data-expanded={expanded}
      data-pinned={primaryRailPinned}
      data-collapsible={expanded ? "" : "icon"}
      className={cn(
        "group fixed top-(--impersonation-banner-height) bottom-0 left-0 z-40 flex w-[var(--admin-primary-rail-collapsed-width)] flex-col overflow-hidden bg-transparent transition-[width,background-color,box-shadow,border-radius] duration-[80ms] ease-[cubic-bezier(0.71,0,0.17,1)] motion-reduce:transition-none",
        expanded && "w-[var(--admin-primary-rail-expanded-width)]",
        floating &&
          "rounded-r-xl bg-bg-canvas shadow-[0_10px_32px_rgba(15,23,42,0.10)]",
      )}
      onMouseEnter={() => {
        if (!ignoreMouseEnter.current) openRail();
      }}
      onMouseLeave={() => {
        ignoreMouseEnter.current = false;
        closeRail();
      }}
      onFocus={(event) => {
        if (event.target.matches(":focus-visible")) openRail();
      }}
      onBlur={(event) => {
        if (event.currentTarget.contains(event.relatedTarget)) return;
        if (menuOpen) return;
        closeRail();
      }}
    >
      {onToggleCollapsed && (
        // Inside the nav (which clips overflow), so hovering the chevron
        // keeps the rail hovered and a collapsed rail's overlay open.
        <SidebarHoverArrowTrigger
          isVisible={hovered}
          collapsed={!forceExpanded}
          onToggle={onToggleCollapsed}
          buttonLabel={
            forceExpanded ? "Hide main navigation" : "Show main navigation"
          }
          className="has-[:focus-visible]:opacity-100"
          buttonClassName="right-0"
        />
      )}
      <div className="flex h-14 items-center px-3">
        <LeftNavOrgSwitcher
          triggerClassName={cn(
            "flex h-8 gap-2.5 px-1 pr-2 text-s-v4",
            "group-data-[collapsible=icon]:!size-8 group-data-[collapsible=icon]:!p-1",
          )}
        />
      </div>

      <div className="flex hide-scrollbar min-h-0 flex-1 flex-col items-start overflow-auto px-3 pt-1 pb-3">
        <SidebarMenu>
          {!hideNewButton && (
            <SidebarMenuItem>
              <AdminLeftNavNewButton
                onMenuOpenChange={setMenuOpen}
                trigger={
                  <SidebarMenuButton className={PRIMARY_RAIL_MENU_BUTTON_CLASS}>
                    <PlusIcon />
                    <span>New</span>
                  </SidebarMenuButton>
                }
              />
            </SidebarMenuItem>
          )}
          {items.map((item) => (
            <PrimaryRailMenuItem
              key={item.id}
              item={item}
              active={activeSectionId === item.id}
              expanded={expanded}
              onSelectSection={selectSection}
            />
          ))}
        </SidebarMenu>
        <AdminRailAgentsSection
          rowClassName={PRIMARY_RAIL_MENU_BUTTON_CLASS}
          expanded={expanded}
          onSelect={collapseTransientRail}
        />
      </div>

      {!hideSettings && (
        <div className="flex flex-col items-start gap-1 px-3 pt-2 pb-3">
          <AnnouncementLetter
            expanded={expanded}
            onOpenChange={onLetterOpenChange}
          />
          <SidebarMenu>
            <AccountPlanRailItem />
            {settingsHref && (
              <PrimaryRailMenuItem
                item={{ ...adminSettingsNavItem, href: settingsHref }}
                active={activeSectionId === "settings"}
                expanded={expanded}
                onSelectSection={selectSection}
              />
            )}
          </SidebarMenu>
        </div>
      )}
    </nav>
  );
};

const AccountPlanRailItem: FC = () => {
  const { pathname } = useRouter();
  const hasPlan = useHasCustomerFacingAccountPlan();
  if (!hasPlan) return null;
  return (
    <SidebarMenuItem>
      <SidebarMenuButton
        asChild
        isActive={pathname === "/account-plan"}
        className={PRIMARY_RAIL_MENU_BUTTON_CLASS}
        aria-current={pathname === "/account-plan" ? "page" : undefined}
      >
        <Link href="/account-plan">
          <FileTextOutlineIcon />
          <span>Account Plan</span>
        </Link>
      </SidebarMenuButton>
    </SidebarMenuItem>
  );
};

const PrimaryRailMenuItem: FC<{
  item: AdminPrimaryNavItem;
  active: boolean;
  expanded: boolean;
  onSelectSection: (sectionId: AdminNavSectionId) => void;
}> = ({ item, active, expanded, onSelectSection }) => {
  const Icon = item.Icon;
  const { setOpen } = useSidebar();
  const [operationsVisited, markOperationsVisited] = useOperationsVisited();
  const hasSecondaryNav =
    item.id === "reporting" ||
    item.id === "recommendations" ||
    item.items.length > 0;

  // "New" indicator: a red dot on the top-right of the Operations icon,
  // shown until the user first opens Operations. Absolutely positioned and
  // left-anchored so it stays on the icon in both the collapsed and expanded
  // rail, and never affects the label layout.
  const showNewDot = item.id === "operations" && !operationsVisited;
  const newDot = showNewDot ? (
    <span
      data-testid="operations-nav-new-dot"
      className="pointer-events-none absolute top-[5px] left-[18px] size-2 rounded-full bg-red-8"
    />
  ) : null;

  const newMessagingEnabled = useNewMessagingEnabled();
  const showNewsfeedBadge = item.id === "newsfeed" && newMessagingEnabled;
  const unreadCount = useMessagingActivityUnreadCount(!showNewsfeedBadge);
  const hasUnread = showNewsfeedBadge && unreadCount > 0;

  const unreadPill =
    hasUnread && expanded ? (
      <span className="ml-auto flex h-[15px] min-w-[22px] shrink-0 items-center justify-center rounded-full bg-brand px-1 text-[10px] leading-none font-semibold tracking-[-0.5px] text-white">
        {unreadCount}
      </span>
    ) : null;
  const unreadDot =
    hasUnread && !expanded ? (
      <span className="pointer-events-none absolute top-[5px] left-[18px] size-2 rounded-full bg-brand" />
    ) : null;

  useEffect(() => {
    if (item.id === "operations" && active) markOperationsVisited();
  }, [item.id, active, markOperationsVisited]);

  const selectItem = () => {
    if (item.id === "operations") markOperationsVisited();
    if (hasSecondaryNav) setOpen(true);
    onSelectSection(item.id);
  };

  if (item.href) {
    return (
      <SidebarMenuItem>
        <SidebarMenuButton
          asChild
          isActive={active}
          className={PRIMARY_RAIL_MENU_BUTTON_CLASS}
          aria-current={active ? "page" : undefined}
          onClick={selectItem}
        >
          <Link href={item.href as React.ComponentProps<typeof Link>["href"]}>
            <Icon />
            {newDot}
            {unreadDot}
            <span>{item.label}</span>
            {unreadPill}
          </Link>
        </SidebarMenuButton>
      </SidebarMenuItem>
    );
  }
  return (
    <SidebarMenuItem>
      <SidebarMenuButton
        isActive={active}
        className={PRIMARY_RAIL_MENU_BUTTON_CLASS}
        aria-current={active ? "page" : undefined}
        onClick={selectItem}
      >
        <Icon />
        {newDot}
        {unreadDot}
        <span>{item.label}</span>
        {unreadPill}
      </SidebarMenuButton>
    </SidebarMenuItem>
  );
};

export default AdminPrimaryNavRail;
